// Hash history that changes the fragment through the History API.
//
// This is history@4.10.1's createHashHistory with ONE behavioural change:
// pushHashPath/replaceHashPath use window.history.pushState/replaceState
// instead of `window.location.hash = ...` / `window.location.replace(...)`.
//
// Why: on iPadOS 27 (WebKit in WKWebView), a fragment-only navigation made
// through `location.hash` or `location.replace()` is no longer treated as a
// same-document navigation. The web view performs a full document load of the
// new URL, so every route change rebooted the whole app. In the iPad
// controller that produced an endless boot loop (Launch -> #/home -> reboot
// -> redirect to #/ -> reboot ...) that showed as a spinner stuck on
// "Verifying user" / "Starting up". Verified on device 2026-10-22 with an
// isolated test page: location.hash and location.replace reload the document,
// history.replaceState does not. history@5 uses the History API for its hash
// history for the same reason; react-router-dom@5 needs history@4, so the
// implementation is vendored here rather than upgraded.
//
// The History API calls are wrapped in a fallback to the old location-based
// methods for environments that refuse pushState (some file:// documents).
// Back/forward on pushState entries fires popstate rather than hashchange, so
// both events are listened to; handleHashChange de-duplicates.
import { createLocation, createPath } from 'history'

function warning(cond, message) {
    if (!cond && process.env.NODE_ENV !== 'production') console.warn(message)
}

function invariant(cond, message) {
    if (!cond) throw new Error(message)
}

function addLeadingSlash(path) {
    return path.charAt(0) === '/' ? path : '/' + path
}

function stripLeadingSlash(path) {
    return path.charAt(0) === '/' ? path.substr(1) : path
}

function hasBasename(path, prefix) {
    return path.toLowerCase().indexOf(prefix.toLowerCase()) === 0 && '/?#'.indexOf(path.charAt(prefix.length)) !== -1
}

function stripBasename(path, prefix) {
    return hasBasename(path, prefix) ? path.substr(prefix.length) : path
}

function stripTrailingSlash(path) {
    return path.charAt(path.length - 1) === '/' ? path.slice(0, -1) : path
}

function createTransitionManager() {
    var prompt = null

    function setPrompt(nextPrompt) {
        warning(prompt == null, 'A history supports only one prompt at a time')
        prompt = nextPrompt
        return function () {
            if (prompt === nextPrompt) prompt = null
        }
    }

    function confirmTransitionTo(location, action, getUserConfirmation, callback) {
        if (prompt != null) {
            var result = typeof prompt === 'function' ? prompt(location, action) : prompt
            if (typeof result === 'string') {
                if (typeof getUserConfirmation === 'function') {
                    getUserConfirmation(result, callback)
                } else {
                    warning(false, 'A history needs a getUserConfirmation function in order to use a prompt message')
                    callback(true)
                }
            } else {
                callback(result !== false)
            }
        } else {
            callback(true)
        }
    }

    var listeners = []

    function appendListener(fn) {
        var isActive = true
        function listener() {
            if (isActive) fn.apply(void 0, arguments)
        }
        listeners.push(listener)
        return function () {
            isActive = false
            listeners = listeners.filter(function (item) { return item !== listener })
        }
    }

    function notifyListeners() {
        var args = Array.prototype.slice.call(arguments)
        listeners.forEach(function (listener) { listener.apply(void 0, args) })
    }

    return { setPrompt, confirmTransitionTo, appendListener, notifyListeners }
}

var canUseDOM = !!(typeof window !== 'undefined' && window.document && window.document.createElement)

function getConfirmation(message, callback) {
    callback(window.confirm(message)) // eslint-disable-line no-alert
}

function supportsGoWithoutReloadUsingHash() {
    return window.navigator.userAgent.indexOf('Firefox') === -1
}

var HashChangeEvent = 'hashchange'
var PopStateEvent = 'popstate'

var HashPathCoders = {
    hashbang: {
        encodePath: function (path) { return path.charAt(0) === '!' ? path : '!/' + stripLeadingSlash(path) },
        decodePath: function (path) { return path.charAt(0) === '!' ? path.substr(1) : path }
    },
    noslash: { encodePath: stripLeadingSlash, decodePath: addLeadingSlash },
    slash: { encodePath: addLeadingSlash, decodePath: addLeadingSlash }
}

function stripHash(url) {
    var hashIndex = url.indexOf('#')
    return hashIndex === -1 ? url : url.slice(0, hashIndex)
}

function getHashPath() {
    var href = window.location.href
    var hashIndex = href.indexOf('#')
    return hashIndex === -1 ? '' : href.substring(hashIndex + 1)
}

// The two functions that differ from history@4. See the header comment.
function pushHashPath(path) {
    var url = stripHash(window.location.href) + '#' + path
    try {
        window.history.pushState(null, '', url)
    } catch (e) {
        console.warn('[hashHistory] pushState refused, falling back to location.hash', e && e.message)
        window.location.hash = path
    }
}

function replaceHashPath(path) {
    var url = stripHash(window.location.href) + '#' + path
    try {
        window.history.replaceState(null, '', url)
    } catch (e) {
        console.warn('[hashHistory] replaceState refused, falling back to location.replace', e && e.message)
        window.location.replace(url)
    }
}

export default function createHashHistory(props) {
    if (props === void 0) props = {}

    invariant(canUseDOM, 'Hash history needs a DOM')
    var globalHistory = window.history
    var canGoWithoutReload = supportsGoWithoutReloadUsingHash()
    var getUserConfirmation = props.getUserConfirmation === void 0 ? getConfirmation : props.getUserConfirmation
    var hashType = props.hashType === void 0 ? 'slash' : props.hashType
    var basename = props.basename ? stripTrailingSlash(addLeadingSlash(props.basename)) : ''
    var encodePath = HashPathCoders[hashType].encodePath
    var decodePath = HashPathCoders[hashType].decodePath

    function getDOMLocation() {
        var path = decodePath(getHashPath())
        warning(!basename || hasBasename(path, basename), 'You are attempting to use a basename on a page whose URL path does not begin with the basename. Expected path "' + path + '" to begin with "' + basename + '".')
        if (basename) path = stripBasename(path, basename)
        return createLocation(path)
    }

    var transitionManager = createTransitionManager()

    function setState(nextState) {
        Object.assign(history, nextState)
        history.length = globalHistory.length
        transitionManager.notifyListeners(history.location, history.action)
    }

    var forceNextPop = false
    var ignorePath = null

    function locationsAreEqual(a, b) {
        return a.pathname === b.pathname && a.search === b.search && a.hash === b.hash
    }

    function handleHashChange() {
        var path = getHashPath()
        var encodedPath = encodePath(path)

        if (path !== encodedPath) {
            replaceHashPath(encodedPath)
        } else {
            var location = getDOMLocation()
            var prevLocation = history.location
            if (!forceNextPop && locationsAreEqual(prevLocation, location)) return
            if (ignorePath === createPath(location)) return
            ignorePath = null
            handlePop(location)
        }
    }

    function handlePop(location) {
        if (forceNextPop) {
            forceNextPop = false
            setState()
        } else {
            var action = 'POP'
            transitionManager.confirmTransitionTo(location, action, getUserConfirmation, function (ok) {
                if (ok) {
                    setState({ action: action, location: location })
                } else {
                    revertPop(location)
                }
            })
        }
    }

    function revertPop(fromLocation) {
        var toLocation = history.location
        var toIndex = allPaths.lastIndexOf(createPath(toLocation))
        if (toIndex === -1) toIndex = 0
        var fromIndex = allPaths.lastIndexOf(createPath(fromLocation))
        if (fromIndex === -1) fromIndex = 0
        var delta = toIndex - fromIndex
        if (delta) {
            forceNextPop = true
            go(delta)
        }
    }

    // Ensure the hash is encoded properly before doing anything else.
    var path = getHashPath()
    var encodedPath = encodePath(path)
    if (path !== encodedPath) replaceHashPath(encodedPath)
    var initialLocation = getDOMLocation()
    var allPaths = [createPath(initialLocation)]

    function createHref(location) {
        var baseTag = document.querySelector('base')
        var href = ''
        if (baseTag && baseTag.getAttribute('href')) {
            href = stripHash(window.location.href)
        }
        return href + '#' + encodePath(basename + createPath(location))
    }

    function push(path, state) {
        warning(state === undefined, 'Hash history cannot push state; it is ignored')
        var action = 'PUSH'
        var location = createLocation(path, undefined, undefined, history.location)
        transitionManager.confirmTransitionTo(location, action, getUserConfirmation, function (ok) {
            if (!ok) return
            var path = createPath(location)
            var encodedPath = encodePath(basename + path)
            var hashChanged = getHashPath() !== encodedPath

            if (hashChanged) {
                ignorePath = path
                pushHashPath(encodedPath)
                var prevIndex = allPaths.lastIndexOf(createPath(history.location))
                var nextPaths = allPaths.slice(0, prevIndex + 1)
                nextPaths.push(path)
                allPaths = nextPaths
                setState({ action: action, location: location })
            } else {
                warning(false, 'Hash history cannot PUSH the same path; a new entry will not be added to the history stack')
                setState()
            }
        })
    }

    function replace(path, state) {
        warning(state === undefined, 'Hash history cannot replace state; it is ignored')
        var action = 'REPLACE'
        var location = createLocation(path, undefined, undefined, history.location)
        transitionManager.confirmTransitionTo(location, action, getUserConfirmation, function (ok) {
            if (!ok) return
            var path = createPath(location)
            var encodedPath = encodePath(basename + path)
            var hashChanged = getHashPath() !== encodedPath

            if (hashChanged) {
                ignorePath = path
                replaceHashPath(encodedPath)
            }

            var prevIndex = allPaths.indexOf(createPath(history.location))
            if (prevIndex !== -1) allPaths[prevIndex] = path
            setState({ action: action, location: location })
        })
    }

    function go(n) {
        warning(canGoWithoutReload, 'Hash history go(n) causes a full page reload in this browser')
        globalHistory.go(n)
    }

    function goBack() { go(-1) }
    function goForward() { go(1) }

    var listenerCount = 0

    function checkDOMListeners(delta) {
        listenerCount += delta
        if (listenerCount === 1 && delta === 1) {
            window.addEventListener(HashChangeEvent, handleHashChange)
            window.addEventListener(PopStateEvent, handleHashChange)
        } else if (listenerCount === 0) {
            window.removeEventListener(HashChangeEvent, handleHashChange)
            window.removeEventListener(PopStateEvent, handleHashChange)
        }
    }

    var isBlocked = false

    function block(prompt) {
        if (prompt === void 0) prompt = false
        var unblock = transitionManager.setPrompt(prompt)
        if (!isBlocked) {
            checkDOMListeners(1)
            isBlocked = true
        }
        return function () {
            if (isBlocked) {
                isBlocked = false
                checkDOMListeners(-1)
            }
            return unblock()
        }
    }

    function listen(listener) {
        var unlisten = transitionManager.appendListener(listener)
        checkDOMListeners(1)
        return function () {
            checkDOMListeners(-1)
            unlisten()
        }
    }

    var history = {
        length: globalHistory.length,
        action: 'POP',
        location: initialLocation,
        createHref: createHref,
        push: push,
        replace: replace,
        go: go,
        goBack: goBack,
        goForward: goForward,
        block: block,
        listen: listen
    }
    return history
}
